import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";
import { slugify } from "../../utils/slugify";
import { getRequest } from "../../services/api";
import { formatCategoryName } from "../../utils/stringFormat";
import RelatedBlogs from "../../components/Blogs/RelatedBlogs/RelatedBlogs";
import Ads from "../../components/Ads/Ads";
import "./BlogShared.css";
import "./AllBlogs.css";

interface BlogPost {
  _id: string;
  slug: string;
  header_title: string;
  url_title: string;
  category_name: string;
  test_category_name: string;
  sub_category_name: string;
  image: string;
  posted_by: string;
  updated_at: string;
  views: number;
  likes: number;
}

/* ── Category config ────────────────────────────────────────────── */
const CAT_CONFIG: Record<string, { badge: string; badgeColor: string; emoji: string; sub: string }> = {
  mcqs:               { badge: "MCQ Practice",        badgeColor: "#f0eeff,#7c3aed", emoji: "🎯", sub: "Sharpen your skills with topic-wise multiple choice questions" },
  test_series:        { badge: "Test Series",          badgeColor: "#e6faf7,#0d9e8a", emoji: "📋", sub: "Full-length mock tests for all major competitive exams" },
  posts:              { badge: "Blog Posts",           badgeColor: "#fff7ed,#ea580c", emoji: "📰", sub: "Insights, strategies and guides from our editorial team" },
  technical_posts:    { badge: "Technical Blogs",      badgeColor: "#eff6ff,#2563eb", emoji: "💻", sub: "Deep dives into tech, tools and engineering topics" },
  interview_questions:{ badge: "Interview Questions",  badgeColor: "#f0fdf4,#16a34a", emoji: "🤝", sub: "Ace your next interview with targeted question banks" },
};

const getLinkForBlog = (blog: BlogPost, category: string | undefined) => {
  if (category === "test_series") {
    return `/online-tests/${slugify(blog.test_category_name)}/${blog.slug}`;
  }
  if (category === "mcqs") {
    return `/mcqs/mcqs-post/${slugify(blog.category_name)}/${slugify(blog.sub_category_name.replace(/\0/g, "").trim())}/${blog.slug}`;
  }
  return `/blogs/blog-post/${slugify(blog.category_name)}/${slugify(blog.sub_category_name)}/${blog.slug}`;
};

const AllBlogs: React.FC = () => {
  const [postsBlogs, setPostsBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading]       = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { category } = useParams();

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const response = await getRequest("/blogs", { category: `${category}` });
        setPostsBlogs(response.data.blogs);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [category]);

  const cfg       = CAT_CONFIG[category || ""] || { badge: "Blogs", badgeColor: "#e6faf7,#0d9e8a", emoji: "📄", sub: "" };
  const [badgeBg, badgeColor] = cfg.badgeColor.split(",");

  const filtered = searchQuery
    ? postsBlogs.filter(b => b.header_title.toLowerCase().includes(searchQuery.toLowerCase()))
    : postsBlogs;

  return (
    <>
      <Helmet>
        <title>{formatCategoryName(category)} — Acadebility</title>
        <meta name="description" content={`All ${formatCategoryName(category)} on Acadebility`} />
      </Helmet>

      {/* ── Hero ── */}
      <div className="bl-hero">
        <div className="bl-hero-inner">
          <nav className="bl-breadcrumb">
            <Link to="/">Home</Link><span>/</span>
            <span className="bl-breadcrumb-current">{formatCategoryName(category)}</span>
          </nav>
          <h1 className="bl-hero-title">All {formatCategoryName(category)}</h1>
          <p className="bl-hero-sub">{cfg.sub || "Your learning journey starts here."}</p>
          <div className="bl-hero-pills">
            <div className="bl-hero-pill"><span>{cfg.emoji}</span>{postsBlogs.length} {cfg.badge}</div>
          </div>
        </div>
        <div className="bl-hero-blob bl-hero-blob-1" />
        <div className="bl-hero-blob bl-hero-blob-2" />
      </div>

      {/* ── Page body ── */}
      <div className="bl-page">
        <div className="bl-layout">

          {/* ── Main ── */}
          <div className="bl-main">

            {/* Search + count bar */}
            <div className="ab-toolbar">
              <div className="ab-count">
                {loading ? "Loading…" : `${filtered.length} result${filtered.length !== 1 ? "s" : ""}`}
              </div>
              <div className="ab-search-wrap">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="ab-search"
                  placeholder={`Search ${cfg.badge}…`}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Cards grid */}
            <div className="ab-grid">
              {loading ? (
                Array.from({ length: 9 }).map((_, i) => (
                  <div className="ab-skeleton-card" key={i}>
                    <Skeleton height={180} style={{ borderRadius: "12px 12px 0 0" }} />
                    <div style={{ padding: "14px 16px" }}>
                      <Skeleton height={16} style={{ marginBottom: 8 }} />
                      <Skeleton width="60%" height={13} />
                    </div>
                  </div>
                ))
              ) : filtered.length === 0 ? (
                <div className="ab-empty">
                  <span className="ab-empty-icon">{cfg.emoji}</span>
                  <h3>No {cfg.badge} found</h3>
                  <p>{searchQuery ? "Try a different search term." : "Check back soon!"}</p>
                </div>
              ) : (
                filtered.map(blog => (
                  <Link
                    key={blog._id}
                    to={getLinkForBlog(blog, category)}
                    state={{ id: blog._id }}
                    className="ab-card-link"
                  >
                    <div className="ab-card">
                      {/* Image area */}
                      <div className="ab-card-img-wrap">
                        {blog.image ? (
                          <img
                            src={blog.image}
                            alt={blog.header_title}
                            className="ab-card-img"
                            loading="lazy"
                          />
                        ) : (
                          <div className="ab-card-img-placeholder">{cfg.emoji}</div>
                        )}
                        {/* Category badge */}
                        <span
                          className="ab-card-cat"
                          style={{ background: badgeBg, color: badgeColor }}
                        >
                          {cfg.badge}
                        </span>
                      </div>

                      {/* Body */}
                      <div className="ab-card-body">
                        <h3 className="ab-card-title">{blog.header_title}</h3>
                        {blog.posted_by && (
                          <p className="ab-card-author">✍️ {blog.posted_by}</p>
                        )}
                        <div className="ab-card-meta">
                          <span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            {blog.views ?? 0} Views
                          </span>
                          <span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                            {blog.likes ?? 0} Likes
                          </span>
                          <span className="ab-card-read">Read →</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>

          </div>

          {/* ── Sidebar ── */}
          <aside className="bl-sidebar">
            <Ads slot="8537390283" />
            <RelatedBlogs />
          </aside>

        </div>
      </div>
    </>
  );
};

export default AllBlogs;
